import { Dispatch } from 'redux';
import { CancelFunctions } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import { Availability, PreservedAvailability } from 'components/common/types/Deal.types';
import { CampaignPlannerMode } from 'store/dealManagement/types';
import {
  changeBackupDealLineAvailabilityWithoutAssets,
  changeCurrentLineAvailability,
  changeFormParams,
  clearDisclaimer,
  clearLineAssets,
  resetDealLineAvailability,
  updateCurrentLineAndBackupLineProperty,
} from 'store/dealManagement/reducer';
import { getIsValidDirectSalesAvailabilityRequest } from 'store/dealManagement/selectors';
import { addToastNotification } from 'store/notification/reducer';
import { checkDirectSalesAvailability } from 'modules/api/directSalesCampaign';
import { groupDirectSalesAssets } from 'components/pages/Planner/hooks/utils/groupDirectSalesAssets';
import { isDirectSalesLinePriced } from 'components/pages/Planner/hooks/utils/getIsDirectSalesLinePriced';
import { getIsDraftStatus } from 'components/pages/Planner/PlannerLineActions/DirectSalesPlannerLineActions/utils';
import { batch } from 'react-redux';

const UNAVAILABLE_FRAME_SELECTION_MESSAGE =
  'There are no frames available for the targeting criteria you have chosen. Please revise your targeting selection.';

let lastRequestedLineId: string | null = null;

export const checkDirectSalesAvailabilityAction =
  ({ overrideLineId, cancelFunctions }: { overrideLineId?: string; cancelFunctions: CancelFunctions }) =>
  async (dispatch: Dispatch, getState: () => Store): Promise<void> => {
    const state = getState();
    const { marketId } = state.publisher.configuration;
    const campaignId = state.dealManagement.commonDeal.dealId;
    const currentLineId = state.dealManagement.commonDeal.currentLine.lineId;
    const lineId = overrideLineId ?? currentLineId;
    const lineStatus = state.dealManagement.directSales.state.status;
    const isOverviewMode = state.dealManagement.campaignPlannerMode === CampaignPlannerMode.OVERVIEW;
    const isValid = getIsValidDirectSalesAvailabilityRequest(state);

    if (!isValid) {
      cancelFunctions.CHECK_DIRECT_SALES_AVAILABILITY?.();
      dispatch(resetDealLineAvailability());
      dispatch(clearLineAssets(lineId));
      dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'hasAvailabilityResult', value: false }));
      return;
    }

    if (isDirectSalesLinePriced(lineStatus) && !getIsDraftStatus(lineStatus)) return;

    batch(() => {
      dispatch(clearDisclaimer());
      dispatch(changeFormParams({ isForecastedAllocation: false }));
      dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'isFetchingAvailability', value: true }));
      dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'hasAvailabilityResult', value: false }));
    });

    lastRequestedLineId = lineId;

    try {
      const data = await checkDirectSalesAvailability(
        marketId,
        campaignId,
        lineId,
        !isOverviewMode ? cancelFunctions : {},
      );

      const hasUniverseInventory = (data.universeFrames ?? 0) > 0;

      if (!data.availableFrames) {
        dispatch(
          addToastNotification({
            description: UNAVAILABLE_FRAME_SELECTION_MESSAGE,
            timeout: 10000,
            type: 'Warning',
          }),
        );
      }

      if (!data.availableFrames && !hasUniverseInventory) {
        dispatch(clearLineAssets(lineId));
        throw new Error(UNAVAILABLE_FRAME_SELECTION_MESSAGE);
      }

      const { assets, unavailableAssets } = groupDirectSalesAssets(data.assets);

      if (lastRequestedLineId === lineId) {
        dispatch(
          changeCurrentLineAvailability({
            ...data,
            assets,
            unavailableAssets,
            missingAssets: [],
            availableFrames: assets.length,
          }),
        );
      }

      dispatch(
        changeBackupDealLineAvailabilityWithoutAssets({
          lineId,
          availability: data as Availability & PreservedAvailability,
        }),
      );
      batch(() => {
        dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'hasAvailabilityResult', value: true }));
        dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'isFetchingAvailability', value: false }));
      });
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        batch(() => {
          dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'isFetchingAvailability', value: false }));
          dispatch(updateCurrentLineAndBackupLineProperty({ lineId, type: 'hasAvailabilityResult', value: true }));
          dispatch(resetDealLineAvailability());
          dispatch(clearLineAssets(lineId));
        });
      }
    }
  };
